import { z } from 'zod';
import { brandUrlSchema } from './branding.js';

/**
 * The component registry: the only components a layout may contain. Each entry is data the
 * API validates against, the web renders with a fixed React component, and the AI builder
 * reads to know what exists. There is no way to add a component at run time: generated
 * widgets are compositions of these.
 */

/**
 * Where a layout is shown. The home dashboard and custom pages are about the customer's
 * account; the two server surfaces are one server's overview and console pages, rendered
 * inside that server's frame (hero, power buttons and tabs stay outside the layout).
 */
export const LAYOUT_SURFACES = ['dashboard', 'page', 'server-overview', 'server-console'] as const;
export type LayoutSurface = (typeof LAYOUT_SURFACES)[number];
export const LAYOUT_SURFACE_LABELS: Record<LayoutSurface, string> = {
  dashboard: 'Home dashboard',
  page: 'Custom page',
  'server-overview': 'Server overview',
  'server-console': 'Server console',
};
const HOME: readonly LayoutSurface[] = ['dashboard', 'page'];
const SERVER: readonly LayoutSurface[] = ['server-overview', 'server-console'];

export const COMPONENT_CATEGORIES = [
  'layout',
  'content',
  'server',
  'servers',
  'monitoring',
  'account',
  'announcements',
  'links',
] as const;
export type ComponentCategory = (typeof COMPONENT_CATEGORIES)[number];

export const COMPONENT_CATEGORY_LABELS: Record<ComponentCategory, string> = {
  layout: 'Layout',
  content: 'Text and content',
  server: 'This server',
  servers: 'Server information',
  monitoring: 'Monitoring',
  account: 'Account',
  announcements: 'Announcements',
  links: 'Links and buttons',
};

/** Props every component carries. */
export const visibilitySchema = z.object({
  /** Who sees it: everyone, only administrators, or only non-admin customers. */
  audience: z.enum(['all', 'admins', 'customers']).default('all'),
  hideOnMobile: z.boolean().default(false),
});
export type Visibility = z.infer<typeof visibilitySchema>;
const visibility = visibilitySchema.default({ audience: 'all', hideOnMobile: false });

const text = (max: number) => z.string().trim().max(max).default('');
const urlProp = brandUrlSchema;
/**
 * An image: one of the tenant's uploaded assets. External image URLs aren't offered: the
 * panel's Content-Security-Policy only loads images from its own origin, and serving them
 * from the asset store keeps them tenant-scoped and sanitised.
 */
const imageSchema = z
  .object({
    asset: z.uuid().nullable().default(null),
    alt: text(200),
  })
  .default({ asset: null, alt: '' });

/** Rows the server details card can show. */
export const SERVER_FACTS = [
  'status',
  'address',
  'players',
  'version',
  'game',
  'location',
  'sftp',
] as const;
/** Shortcuts the quick actions card can offer. */
export const SERVER_QUICK_ACTIONS = ['backup', 'console', 'files', 'startup'] as const;

export const SERVER_FIELDS = [
  'status',
  'game',
  'address',
  'node',
  'players',
  'memory',
  'disk',
  'cpu',
] as const;

export interface ComponentDefinition<S extends z.ZodType = z.ZodType> {
  type: string;
  label: string;
  category: ComponentCategory;
  /** One sentence the library and the AI builder show. */
  description: string;
  /** Whether it holds children (sections, containers, columns). */
  container: boolean;
  /** Only these child types are allowed (columns only hold column nodes). */
  childTypes?: readonly string[];
  /** Only allowed inside these parents (a column lives in columns). */
  parentTypes?: readonly string[];
  /** Hearth data it reads (documented dependency for the AI builder and the inspector). */
  dataSources: readonly string[];
  props: S;
  /** Hidden from the library (structural nodes the builder adds itself). */
  internal?: boolean;
  /** The surfaces it may be placed on; every surface when absent. */
  surfaces?: readonly LayoutSurface[];
  /**
   * Surfaces where the layout must contain it exactly once, never hidden: core functionality
   * (the console, status alerts) can be moved but not removed.
   */
  required?: readonly LayoutSurface[];
  /** At most one per layout (it owns page state, such as the console's log). */
  unique?: boolean;
}

function define<S extends z.ZodType>(d: ComponentDefinition<S>): ComponentDefinition<S> {
  return d;
}

export const COMPONENTS = [
  // ---- layout ----
  define({
    type: 'section',
    label: 'Section',
    category: 'layout',
    description: 'A titled group of components with optional description.',
    container: true,
    dataSources: [],
    props: z.object({
      title: text(80),
      description: text(200),
      width: z.enum(['full', 'narrow']).default('full'),
      background: z.enum(['none', 'card', 'tinted']).default('none'),
      visibility,
    }),
  }),
  define({
    type: 'container',
    label: 'Container',
    category: 'layout',
    description: 'A plain box that stacks its children with spacing; use it to group things.',
    container: true,
    dataSources: [],
    props: z.object({
      gap: z.enum(['none', 'sm', 'md', 'lg']).default('md'),
      padding: z.enum(['none', 'sm', 'md', 'lg']).default('none'),
      background: z.enum(['none', 'card', 'tinted']).default('none'),
      visibility,
    }),
  }),
  define({
    type: 'columns',
    label: 'Columns',
    category: 'layout',
    description: 'Two or three columns side by side; each column holds its own components.',
    container: true,
    childTypes: ['column'],
    dataSources: [],
    props: z.object({
      count: z.union([z.literal(2), z.literal(3)]).default(2),
      /** Relative widths; "1:2" makes the second column twice as wide. */
      ratio: z.enum(['equal', '1:2', '2:1']).default('equal'),
      gap: z.enum(['sm', 'md', 'lg']).default('md'),
      visibility,
    }),
  }),
  define({
    type: 'column',
    label: 'Column',
    category: 'layout',
    description: 'One column of a Columns block.',
    container: true,
    parentTypes: ['columns'],
    dataSources: [],
    internal: true,
    props: z.object({}),
  }),
  define({
    type: 'divider',
    label: 'Divider',
    category: 'layout',
    description: 'A horizontal rule.',
    container: false,
    dataSources: [],
    props: z.object({ visibility }),
  }),
  define({
    type: 'spacer',
    label: 'Spacer',
    category: 'layout',
    description: 'Empty vertical space.',
    container: false,
    dataSources: [],
    props: z.object({ size: z.enum(['sm', 'md', 'lg', 'xl']).default('md'), visibility }),
  }),
  // ---- content ----
  define({
    type: 'heading',
    label: 'Heading',
    category: 'content',
    description: 'A heading line.',
    container: false,
    dataSources: [],
    props: z.object({
      text: text(120),
      level: z.union([z.literal(1), z.literal(2), z.literal(3)]).default(2),
      align: z.enum(['left', 'center', 'right']).default('left'),
      visibility,
    }),
  }),
  define({
    type: 'text',
    label: 'Text',
    category: 'content',
    description: 'A paragraph or two. Markdown is supported (bold, lists, links); no HTML.',
    container: false,
    dataSources: [],
    props: z.object({
      markdown: text(5000),
      align: z.enum(['left', 'center', 'right']).default('left'),
      size: z.enum(['sm', 'md', 'lg']).default('md'),
      tone: z.enum(['default', 'muted']).default('default'),
      visibility,
    }),
  }),
  define({
    type: 'image',
    label: 'Image',
    category: 'content',
    description: 'An uploaded image or an https image URL.',
    container: false,
    dataSources: [],
    props: z.object({
      image: imageSchema,
      width: z.enum(['auto', 'full', 'half', 'third']).default('auto'),
      align: z.enum(['left', 'center', 'right']).default('left'),
      rounded: z.boolean().default(true),
      linkUrl: urlProp,
      visibility,
    }),
  }),
  define({
    type: 'info-card',
    label: 'Information card',
    category: 'content',
    description: 'A card with an icon, a title and a short body.',
    container: false,
    dataSources: [],
    props: z.object({
      icon: z
        .enum(['info', 'book', 'life-buoy', 'rocket', 'shield', 'star', 'zap', 'users', 'none'])
        .default('info'),
      title: text(80),
      body: text(600),
      linkUrl: urlProp,
      linkLabel: text(40),
      visibility,
    }),
  }),
  define({
    type: 'cta',
    label: 'Call to action',
    category: 'content',
    description: 'A banner with a headline, a sentence and a prominent button.',
    container: false,
    dataSources: [],
    props: z.object({
      title: text(80),
      body: text(300),
      buttonLabel: text(40),
      buttonUrl: urlProp,
      /** A route inside the panel instead of a URL, e.g. "/ai" or "/custom/getting-started". */
      buttonPath: z
        .string()
        .trim()
        .max(200)
        .regex(/^(\/[^\s]*)?$/, 'Paths start with /')
        .default(''),
      appearance: z.enum(['brand', 'tinted', 'outline']).default('brand'),
      visibility,
    }),
  }),
  // ---- announcements ----
  define({
    type: 'announcement',
    label: 'Announcement banner',
    category: 'announcements',
    description: 'A dismissible banner for news or maintenance notices.',
    container: false,
    dataSources: [],
    props: z.object({
      title: text(80),
      description: text(500),
      linkUrl: urlProp,
      linkLabel: text(40),
      appearance: z.enum(['info', 'success', 'warning', 'brand', 'neutral']).default('info'),
      dismissible: z.boolean().default(true),
      visibility,
    }),
  }),
  // ---- links ----
  define({
    type: 'button',
    label: 'Button',
    category: 'links',
    description: 'A single button linking to a URL or a panel page.',
    container: false,
    dataSources: [],
    props: z.object({
      label: text(40),
      url: urlProp,
      path: z
        .string()
        .trim()
        .max(200)
        .regex(/^(\/[^\s]*)?$/, 'Paths start with /')
        .default(''),
      variant: z.enum(['default', 'secondary', 'outline', 'ghost']).default('default'),
      size: z.enum(['sm', 'default', 'lg']).default('default'),
      align: z.enum(['left', 'center', 'right']).default('left'),
      visibility,
    }),
  }),
  define({
    type: 'link-list',
    label: 'Link list',
    category: 'links',
    description: 'A list of labelled links (support, Discord, documentation, status page).',
    container: false,
    dataSources: [],
    props: z.object({
      title: text(60),
      links: z
        .array(
          z.object({
            label: z.string().trim().min(1).max(60),
            url: urlProp,
            path: z
              .string()
              .trim()
              .max(200)
              .regex(/^(\/[^\s]*)?$/)
              .default(''),
            description: text(120),
            icon: z
              .enum(['link', 'message-circle', 'book', 'life-buoy', 'globe', 'mail', 'activity'])
              .default('link'),
          }),
        )
        .max(12)
        .default([]),
      layout: z.enum(['list', 'grid']).default('list'),
      visibility,
    }),
  }),
  define({
    type: 'support-links',
    label: 'Support links',
    category: 'links',
    description: 'The provider’s support, documentation and website links from Branding.',
    container: false,
    dataSources: ['branding'],
    props: z.object({ title: text(60), visibility }),
  }),
  // ---- servers ----
  define({
    type: 'server-overview',
    surfaces: HOME,
    label: 'Server overview',
    category: 'servers',
    description: 'Counts of the viewer’s servers: online, offline and needing attention.',
    container: false,
    dataSources: ['client.servers', 'server.live'],
    props: z.object({ title: text(60), visibility }),
  }),
  define({
    type: 'server-list',
    surfaces: HOME,
    label: 'Server list',
    category: 'servers',
    description: 'The viewer’s servers as rows or cards, with live status and chosen details.',
    container: false,
    dataSources: ['client.servers', 'server.live'],
    props: z.object({
      title: text(60),
      display: z.enum(['cards', 'rows', 'compact']).default('rows'),
      fields: z
        .array(z.enum(SERVER_FIELDS))
        .max(8)
        .default(['status', 'game', 'address', 'players']),
      sort: z.enum(['name', 'game', 'status']).default('name'),
      limit: z.number().int().min(1).max(50).default(12),
      showLink: z.boolean().default(true),
      visibility,
    }),
  }),
  define({
    type: 'online-count',
    surfaces: HOME,
    label: 'Online server count',
    category: 'servers',
    description: 'One big number: how many of the viewer’s servers are online.',
    container: false,
    dataSources: ['client.servers', 'server.live'],
    props: z.object({ label: text(60), visibility }),
  }),
  define({
    type: 'server-status',
    surfaces: HOME,
    label: 'Server status',
    category: 'servers',
    description: 'One server’s state and chosen metrics; pick a server or let it choose.',
    container: false,
    dataSources: ['client.servers', 'server.live', 'server.resources'],
    props: z.object({
      /** The server’s 8-character identifier, or empty for the viewer’s first server. */
      server: z.string().trim().max(36).default(''),
      metrics: z
        .array(z.enum(['cpu', 'memory', 'disk', 'players', 'uptime']))
        .max(5)
        .default(['cpu', 'memory', 'players']),
      mode: z.enum(['compact', 'expanded']).default('expanded'),
      visibility,
    }),
  }),
  define({
    type: 'recent-activity',
    surfaces: HOME,
    label: 'Recent activity',
    category: 'servers',
    description: 'The viewer’s latest account and server activity.',
    container: false,
    dataSources: ['client.account.activity'],
    props: z.object({
      title: text(60),
      limit: z.number().int().min(3).max(20).default(8),
      visibility,
    }),
  }),
  define({
    type: 'recent-backups',
    surfaces: HOME,
    label: 'Recent backups',
    category: 'servers',
    description: 'The latest backups across the viewer’s servers.',
    container: false,
    dataSources: ['client.servers', 'client.servers.backups'],
    props: z.object({
      title: text(60),
      limit: z.number().int().min(3).max(20).default(5),
      visibility,
    }),
  }),
  // ---- monitoring ----
  define({
    type: 'resource-summary',
    surfaces: HOME,
    label: 'Resource usage summary',
    category: 'monitoring',
    description: 'Memory, disk and CPU allocated to the viewer’s servers, with live use.',
    container: false,
    dataSources: ['client.servers', 'server.live'],
    props: z.object({
      title: text(60),
      metrics: z
        .array(z.enum(['memory', 'disk', 'cpu', 'servers']))
        .max(4)
        .default(['servers', 'memory', 'disk']),
      visibility,
    }),
  }),
  define({
    type: 'metric-card',
    surfaces: HOME,
    label: 'Metric card',
    category: 'monitoring',
    description: 'A card of numbers from approved sources (counts, totals, online servers).',
    container: false,
    dataSources: ['client.servers', 'server.live'],
    props: z.object({
      title: text(60),
      metrics: z
        .array(
          z.object({
            source: z.enum([
              'servers.total',
              'servers.online',
              'servers.offline',
              'servers.memoryAllocatedMb',
              'servers.diskAllocatedMb',
              'servers.memoryUsedMb',
              'servers.playersOnline',
            ]),
            label: text(40),
          }),
        )
        .min(1)
        .max(4)
        .default([{ source: 'servers.online', label: 'Online' }]),
      visibility,
    }),
  }),
  // ---- account ----
  define({
    type: 'account-info',
    surfaces: HOME,
    label: 'Account information',
    category: 'account',
    description: 'The viewer’s username, email, security status and links to account pages.',
    container: false,
    dataSources: ['session'],
    props: z.object({
      title: text(60),
      showSecurity: z.boolean().default(true),
      visibility,
    }),
  }),
  define({
    type: 'ai-shortcut',
    label: 'Hearth AI shortcut',
    category: 'account',
    description: 'A card that opens Hearth AI with a suggested prompt.',
    container: false,
    dataSources: [],
    props: z.object({
      title: text(60),
      body: text(200),
      prompt: text(200),
      visibility,
    }),
  }),
  // ---- this server (server overview and console pages) ----
  define({
    type: 'server-alerts',
    label: 'Status alerts',
    category: 'server',
    description:
      'Warnings about the server: suspended, installing, transferring, unreachable node. Required on server pages.',
    container: false,
    surfaces: SERVER,
    required: SERVER,
    unique: true,
    dataSources: ['server', 'server.live'],
    props: z.object({}),
  }),
  define({
    type: 'server-console',
    label: 'Console',
    category: 'server',
    description:
      'The live console with command input, plus the EULA prompt for games that need one. Required on the console page.',
    container: false,
    surfaces: ['server-console'],
    required: ['server-console'],
    unique: true,
    dataSources: ['server', 'server.live', 'server.console'],
    props: z.object({}),
  }),
  define({
    type: 'server-stats',
    label: 'Live stats',
    category: 'server',
    description:
      'What needs attention plus players, performance, disk and uptime tiles for this server.',
    container: false,
    surfaces: SERVER,
    unique: true,
    dataSources: ['server', 'server.live'],
    props: z.object({
      showAttention: z.boolean().default(true),
      visibility,
    }),
  }),
  define({
    type: 'server-usage-charts',
    label: 'Usage charts',
    category: 'server',
    description: 'CPU and memory over the last few minutes for this server.',
    container: false,
    surfaces: SERVER,
    unique: true,
    dataSources: ['server', 'server.live'],
    props: z.object({ visibility }),
  }),
  define({
    type: 'server-facts',
    label: 'Server details',
    category: 'server',
    description: 'Status, address, players, version, game, location and SFTP details to copy.',
    container: false,
    surfaces: SERVER,
    dataSources: ['server', 'server.live', 'server.startup'],
    props: z.object({
      title: text(60),
      fields: z
        .array(z.enum(SERVER_FACTS))
        .max(SERVER_FACTS.length)
        .default([...SERVER_FACTS]),
      visibility,
    }),
  }),
  define({
    type: 'server-quick-actions',
    label: 'Quick actions',
    category: 'server',
    description:
      'Shortcuts the viewer may use: back up now, console, files, startup. Each respects permissions.',
    container: false,
    surfaces: SERVER,
    dataSources: ['server', 'server.backups'],
    props: z.object({
      title: text(60),
      actions: z
        .array(z.enum(SERVER_QUICK_ACTIONS))
        .max(SERVER_QUICK_ACTIONS.length)
        .default([...SERVER_QUICK_ACTIONS]),
      visibility,
    }),
  }),
  define({
    type: 'server-activity',
    label: 'Server activity',
    category: 'server',
    description: 'The latest power actions, file changes and settings edits on this server.',
    container: false,
    surfaces: SERVER,
    dataSources: ['server.activity'],
    props: z.object({
      title: text(60),
      limit: z.number().int().min(3).max(20).default(8),
      visibility,
    }),
  }),
] as const;

export type ComponentType = (typeof COMPONENTS)[number]['type'];

export const COMPONENT_MAP: ReadonlyMap<string, ComponentDefinition> = new Map(
  COMPONENTS.map((c) => [c.type, c as ComponentDefinition]),
);

export function componentDefinition(type: string): ComponentDefinition | undefined {
  return COMPONENT_MAP.get(type);
}

/** Default props for a fresh node of `type` (every prop schema has defaults). */
export function defaultProps(type: string): Record<string, unknown> {
  const def = COMPONENT_MAP.get(type);
  if (!def) throw new Error(`Unknown component type ${type}`);
  return def.props.parse({}) as Record<string, unknown>;
}

/** Whether `type` may be placed on `surface`. */
export function allowedOn(type: string, surface: LayoutSurface): boolean {
  const def = COMPONENT_MAP.get(type);
  return Boolean(def && (!def.surfaces || def.surfaces.includes(surface)));
}

/** Types every layout of `surface` must contain exactly once. */
export function requiredTypes(surface: LayoutSurface): string[] {
  return COMPONENTS.filter((c) =>
    ((c as ComponentDefinition).required ?? []).includes(surface),
  ).map((c) => c.type);
}

/**
 * Component types the library offers (internal structural nodes excluded), limited to those
 * allowed on `surface` when one is given.
 */
export function libraryComponents(surface?: LayoutSurface): ComponentDefinition[] {
  return (COMPONENTS as unknown as ComponentDefinition[]).filter(
    (c) => !c.internal && (!surface || !c.surfaces || c.surfaces.includes(surface)),
  );
}
